type Key = string

/**
 * The array map function, implemented for object. The result will be an array of
 * values resulting from running the given callback on each entry in the object.
 * @param  {Object}   object   The object
 * @param  {Function} callback The callback function
 */
export function mapEntries<U, T>(
	object: { +[Key]: T },
	callback: (currentValue: T, Key) => U
): U[] {
	const mappedValues: U[] = []

	for (let key in object) {
		if (object.hasOwnProperty(key)) {
			mappedValues.push(callback(object[key], key))
		}
	}

	return mappedValues
}

/**
 * The array reduce function implemented for object
 * @param  {Object}   object   The object
 * @param  {Function} callback The callback function
 */
export function reduceObject<U, T>(
	object: $ReadOnly<{ [string]: T }>,
	callback: (previousValue: U, currentValue: T, string) => U,
	initialValue: U
): U {
	let previousValue = initialValue

	for (let key in object) {
		if (object.hasOwnProperty(key)) {
			previousValue = callback(previousValue, object[key], key)
		}
	}

	return previousValue
}

/**
 * Object.values function, but with the flow type maintained.
 */
export function values<T>(object: { [Key]: T }): T[] {
	const values = []

	for (let key in object) {
		if (object.hasOwnProperty(key)) {
			values.push(object[key])
		}
	}

	return values
}

/**
 * The array forEach function, implemented for object. The given callback will be called for each entry
 * @param  {Object}   object   The object
 * @param  {([Key, T]) => mixed} callback The callback function
 */
export function forEachEntry<T>(
	object: { [Key]: T },
	callback: ([Key, T]) => mixed
): void {
	for (let key in object) {
		if (object.hasOwnProperty(key)) {
			callback([key, object[key]])
		}
	}
}

/**
 * Returns a random value from a given array
 * @param {T[]} arr the array to get the random value from
 * @return T | undefined - will return T if a value is present in the array and undefined if no values are present
 */
export function randomFromArray<T>(arr: T[]): T {
	const randomIndex: number = Math.floor(Math.random() * arr.length)
	return arr[randomIndex]
}

// TODO: Move this function to a shared code space as well for simulation actions. It's duplicated on launchpad and sim-creator
/**
 * Returns the expected duration for an action.
 */
export function getActionTimeLength(gameAction: any): ?number {
	switch (gameAction.type) {
		case 'VIDEO_OVERLAY':
		case 'VOCAL_TRACK': {
			return gameAction._mediaDuration
		}
		case 'VIDEO_SCREEN': {
			if (gameAction.looping) {
				return gameAction.timeLimit
			} else {
				return gameAction._mediaDuration
			}
		}
		case 'IMAGE_OVERLAY':
		case 'START_TIMER': {
			return gameAction.duration
		}
		case 'IMAGE_SCREEN':
		case 'MAP_SCREEN':
		case 'CULMINATING_MOMENT_SCREEN':
		case 'NAVIGATION_SCREEN': {
			return gameAction.timeLimit
		}
		default: {
			return null
		}
	}
}

/**
 * Given a hex string and an opacity percentage (number between 0 and 1), will convert to rgba value for that string.
 * Helpful for lightening constant colors.
 * @param {string} hex
 * @param {number} percent between 0 and 1
 */
export const rgba = (hex: string, percent: number): string => {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
	if (result) {
		const { r, g, b } = {
			r: parseInt(result[1], 16),
			g: parseInt(result[2], 16),
			b: parseInt(result[3], 16),
		}
		return `rgba(${r}, ${g}, ${b}, ${percent})`
	}
	return ''
}

/**
 * Returns a number whose value is limited to the given range.
 * @param  {number} number The number to change
 * @param  {number} min    The lower boundary of the output range
 * @param  {number} max    The upper boundary of the output range
 * @return {number}        A number in the range [min, max]
 */
export function clamp(number: number, min: number, max: number): number {
	return Math.min(Math.max(number, min), max)
}

/**
 * aspectRatioWidthCss - restricts the width of an element to fit within targetWidth and targetHeight,
 *  while maintaining the aspectRatio
 *
 * @param  {string} targetWidth - the widest the element can be
 * @param  {string} targetHeight - the tallest the element can be
 * @param  {string} aspectRatio - the aspect ratio the element should have (width / height)
 * @returns string - the css property to achieve the element fitting within the target dimensions while maintaining aspect ratio.
 */
export function aspectRatioWidthCss(
	targetWidth: string,
	targetHeight: string,
	aspectRatio: number
): string {
	return `min(${targetWidth}, calc(${targetHeight} * ${aspectRatio}))`
}

/**
 * aspectRatioHeightCss - restricts the height of an element to fit within targetWidth and targetHeight,
 *  while maintaining the aspectRatio
 *
 * @param  {string} targetWidth - the widest the element can be
 * @param  {string} targetHeight - the tallest the element can be
 * @param  {string} aspectRatio - the aspect ratio the element should have (width / height)
 * @returns string - the css property to achieve the element fitting within the target dimensions while maintaining aspect ratio.
 */
export function aspectRatioHeightCss(
	targetWidth: string,
	targetHeight: string,
	aspectRatio: number
): string {
	return `min(${targetHeight}, calc(${targetWidth} / ${aspectRatio}))`
}

/**
 * areNumbersInDifferentBinsFromZero - determine if two numbers are in different bins based on the the zero and binWidth. Visualization:
 *
 *                     Bin: -1                                   Bin: 0
 *     | <---------- Bin Width = 2 ----------> |                                       |
 * <- zero-2 --------- zero-1 --------------  zero  ----------- zero+1 ------------- zero+2 ->
 *
 * @param {number} num1 - one of the numbers to calculate and compare the bins for
 * @param {number} num2 - the other of the numbers to calculate and compare the bins for
 * @param {number} binWidth - the width of the bins
 * @param {number=0} zero? - where the most negative side of bin 0 should start
 *
 * @return {boolean} - true if the numbers fall in different bins, false otherwise
 */
export function areNumbersInDifferentBinsFromZero(
	num1: number,
	num2: number,
	binWidth: number,
	zero?: number = 0
): boolean {
	return (
		Math.floor((num1 - zero) / binWidth) !==
		Math.floor((num2 - zero) / binWidth)
	)
}
